import React, { useEffect, useRef, useState } from 'react';
import FullCalendar, {
	EventClickArg,
	EventContentArg,
} from '@fullcalendar/react';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import { useAppContext } from '../../../../Services/AppContext';
import { Moment } from 'moment';
import { getMomentDate } from '../../../../Utils/dates.util';
import useTranslation from '../../../../Hooks/useTranslation';
// @ts-ignore
import styles from './TimelineCalendar.module.scss';
import { CalendarView } from '../../Enums/CalendarView.eunm';
import CalendarViewSettingsModal from '../CalendarViewSettings/CalendarViewSettingsModal';
import { CalendarViewSettingsValues } from '../CalendarViewSettings/CalendarViewSettings';
import { CalendarViewIntervalEnum } from '../../Enums/CalendarViewInterval.enum';

export interface TimelineCalendarResource {
	id: string;
	title: string;
	groupKey: string;
}

export interface TimelineCalendarEvent {
	id: string;
	resourceId: string;
	start: string;
	end: string;
	title: string;
	backgroundColor?: string;
	borderColor?: string;
	textColor?: string;
	extendedProps?: Record<string, any>;
}

interface Props {
	resources: TimelineCalendarResource[];
	events: TimelineCalendarEvent[];
	onDateRangeChange: (start: Moment, end: Moment) => void;
	onDateClick: (start: Moment, end: Moment, resourceId: string) => void;
	onEventClick: (data: EventClickArg) => void;
	onViewChange?: (view: CalendarView) => void;
	renderEvent?: (view: EventContentArg) => void;
	resourcesLabel?: string;
	onPrint?: () => void;
}

const TimelineCalendar: React.FC<Props> = ({
	resources,
	events,
	onDateRangeChange,
	onDateClick,
	onEventClick,
	onViewChange,
	resourcesLabel,
	renderEvent,
	onPrint,
}) => {
	const [isCalendarViewSettingsOpen, setIsCalendarViewSettingsOpen] =
		useState(false);
	const [isCalendarViewSettingsAvailable, setIsCalendarViewSettingsAvailable] =
		useState(false);
	const [isPrintAvailable, setIsPrintAvailable] = useState(false);
	const calendarRef = useRef<FullCalendar | null>(null);
	const [slotDuration, setSlotDuration] = useState<string | undefined>(
		CalendarViewIntervalEnum.THIRTY_MINUTES,
	);
	const { language } = useAppContext();
	const { t } = useTranslation();

	const calendarApi = calendarRef?.current?.getApi();

	const removeNativeTooltips = () => {
		// @ts-ignore
		const buttons = document.querySelectorAll('button[title]');
		buttons.forEach((btn: any) => btn.removeAttribute('title'));
	};

	const handleDatesSet = (dateInfo: { startStr: string; endStr: string }) => {
		setTimeout(removeNativeTooltips, 0);
		onDateRangeChange(
			getMomentDate(dateInfo.startStr),
			getMomentDate(dateInfo.endStr),
		);
	};

	const handleOnDateClick = (data: {
		startStr: string;
		endStr: string;
		resource?: {
			id: string;
		};
	}) => {
		if (data.resource?.id) {
			onDateClick(
				getMomentDate(data.startStr),
				getMomentDate(data.endStr),
				data.resource.id,
			);
		}
	};

	const handleOnEventClick = (data: EventClickArg) => {
		onEventClick(data);
	};

	const handleOnChangeCalendarViewSettings = (
		settings: CalendarViewSettingsValues,
	) => {
		setIsCalendarViewSettingsOpen(false);
		setSlotDuration(settings.slotDuration);
	};

	useEffect(() => {
		if (calendarApi?.view.type) {
			onViewChange?.(calendarApi.view.type as CalendarView);
			let printAvailable = false;
			switch (calendarApi.view.type) {
				case CalendarView.TIMELINE_WEEK:
					setIsCalendarViewSettingsAvailable(true);
					setSlotDuration(CalendarViewIntervalEnum.SIX_HOURS);
					break;
				case CalendarView.TIMELINE_MOTH:
					setIsCalendarViewSettingsAvailable(false);
					setSlotDuration(undefined);
					break;
				case CalendarView.LIST_WEEK:
					printAvailable = true;
					setIsCalendarViewSettingsAvailable(false);
					break;
				case CalendarView.TIMELINE_DAY:
				default:
					setIsCalendarViewSettingsAvailable(true);
					setSlotDuration(CalendarViewIntervalEnum.THIRTY_MINUTES);
					break;
			}
			setIsPrintAvailable(printAvailable);
		}
	}, [calendarApi?.view]);

	useEffect(() => {
		setTimeout(removeNativeTooltips, 0);
	}, []);

	const getTextWidth = (text: string, font: string = '14px Arial'): number => {
		// @ts-ignore
		const canvas = document.createElement('canvas');
		const context = canvas.getContext('2d');
		if (!context) return 0;
		context.font = font;
		return context.measureText(text).width;
	};

	const getMaxResourceNameWidth = (
		resources: TimelineCalendarResource[],
		font?: string,
	): number => {
		const max = Math.max(...resources.map(r => getTextWidth(r.title, font)));
		return isFinite(max) ? Math.ceil(max + 70) : 200;
	};

	const resourceAreaWidth = getMaxResourceNameWidth(resources) + 'px';

	let headerToolBarRight =
		'resourceTimelineDay,resourceTimelineWeek,resourceTimelineMonth,listWeek';

	if (isPrintAvailable) {
		headerToolBarRight += ',print';
	}

	return (
		<div className='fullCalendarTimeline'>
			<FullCalendar
				ref={calendarRef}
				schedulerLicenseKey={'0784609858-fcs-1664878357'}
				plugins={[resourceTimelinePlugin, interactionPlugin, listPlugin]}
				initialView='resourceTimelineWeek'
				slotDuration={slotDuration}
				aspectRatio={1.5}
				headerToolbar={{
					left: isCalendarViewSettingsAvailable
						? 'prev,next,today,calendarViewSettings'
						: 'prev,next,today',
					center: 'title',
					right: headerToolBarRight,
				}}
				locale={language}
				noEventsText={t('base.noEventsToDisplay')}
				resources={resources}
				resourceGroupField='groupKey'
				events={events}
				nowIndicator={true}
				selectable={true}
				longPressDelay={100}
				selectMirror={true}
				buttonText={{
					today: t('base.shared.now'),
					month: t('base.months.one'),
					week: t('base.weeks.one'),
					day: t('base.days.one'),
					list: t('base.shared.list'),
				}}
				firstDay={1}
				eventClick={handleOnEventClick}
				datesSet={handleDatesSet}
				select={handleOnDateClick}
				eventClassNames={styles.event}
				resourceLabelClassNames={styles.resourceLabel}
				resourceAreaWidth={resourceAreaWidth}
				resourceAreaHeaderContent={resourcesLabel}
				eventContent={renderEvent}
				customButtons={{
					calendarViewSettings: {
						text: t('component.calendarViewSettings.modalTitle'),
						click: () => setIsCalendarViewSettingsOpen(true),
					},
					print: {
						text: t('component.timelineCalendar.print'),
						click: () => onPrint?.(),
					},
				}}
			/>
			{isCalendarViewSettingsOpen && (
				<CalendarViewSettingsModal
					isOpen
					onChange={handleOnChangeCalendarViewSettings}
					onClose={() => setIsCalendarViewSettingsOpen(false)}
					defaultValues={{
						slotDuration,
					}}
				/>
			)}
		</div>
	);
};

export default TimelineCalendar;
